// THE VOTING WINDOW — when ONE ballot opens and when it closes. The registrar has
// settled that a ballot carries one motion or one election, so a ballot's window IS
// that item's window: this control is per-ballot because the thing it times is.
//
// ── IT IS DISCRETE, AND MANUAL IS THE EASY PATH ────────────────────────────
// A window is not the ballots panel's subject — building drafts and taking them to
// open is — so the whole control lives behind a closed <details>, the same discrete
// disclosure the definition digest already uses one line above it in the same row
// (BallotsPanel's `setup-ballot-digest`). Nothing about it shouts, and an operator
// who never opens it gets today's behaviour unchanged: `manual`, the President's word
// opening the ballot and a Scrutineer's closing it. The summary line carries the
// recorded state in three words, so a ballot that IS on a clock says so without the
// disclosure having to be opened to find out.
//
// ── NO MODAL, DELIBERATELY (Sexton's law) ──────────────────────────────────
// Setting or changing a window on a draft or ready ballot is rebuildable working
// state — it can be set again, moved, or dropped back to by-hand, right up until the
// ballot opens — so it takes NO confirm, exactly as RollPanel's (re)load does. The
// two points of no return on this surface (the freeze and the open) keep theirs.
//
// ── THE BROWSER NEVER TOUCHES A TIMEZONE, AND THAT IS THE CONTRACT ─────────
// Both bounds travel as ZONELESS NZ WALL CLOCK — sixteen characters, exactly
// `YYYY-MM-DDTHH:MM`, no offset and no `Z`. That is precisely what an
// `<input type="datetime-local" step={60}>` holds, so the value is handed to the
// worker VERBATIM: there is no `new Date()` on this path, no offset arithmetic and
// no conversion, because the machine running this browser may be nowhere near
// Auckland and its local zone is evidence about nothing. The worker derives the UTC
// instants. The inversion test below is a STRING comparison, and it is exact for the
// reason a fixed-width wall clock always sorts chronologically: one zone, one width,
// so lexicographic order IS chronological order. Everything this file does to a time
// is slicing characters out of a string it was handed.
//
// ── THE DERIVED INSTANT IS SHOWN, QUIETLY, AND IT IS NOT DECORATION ────────
// The worker answers the two UTC instants it derived, both on the save and on every
// later `GET /api/setup/:electionId`, and they are rendered in the muted role beside
// the wall clock the registrar typed. **This is the only surface on which a human can
// see which daylight-saving offset was applied.** A ballot that opens and closes by
// the clock fails silently by exactly one hour, and an hour is the whole of a
// division; a window that reads "9:00am" and enforces 8:00am is not visible anywhere
// else in this platform. It stays SECONDARY text — the operator's own words are the
// reading, the instant is the check under it — and it is never shown for a time the
// operator has typed but not yet saved, because until the worker has answered, nobody
// here knows what instant it is.
//
// ── WHAT THIS PANEL READS BACK ─────────────────────────────────────────────
// `GET /api/setup/:electionId` now carries a `schedule` object on every row, so a
// freshly loaded page STATES what each ballot's window already is — the fields are
// seeded from it and the recorded window is printed above them. Nothing here is held
// only in component state: a window kept only in the panel that set it would vanish
// on the next refresh, and a registrar would be looking at an empty form and a ballot
// that opens on a clock. `written` (this session's save) is preferred over the
// list's copy for the one case the list cannot cover — the hub does not re-read after
// a save, so its row is the older of the two the moment this panel writes.
//
// ── FIVE THINGS HERE ARE SHARED, AND THE SHARING IS THE POINT ──────────────
// `NZ_WALL_CLOCK`, `wallClockInWords`, `utcInWords`, `windowInWords` and
// `RecordedBound` are EXPORTED, for `OpenPollsPanel` and `ExtendStep` — the two
// surfaces that state a RUNNING poll's window and move it later. They were private
// while this was the only screen that rendered a window; it is not any more. A copy
// would put the wall-clock/instant PAIRING (the one place an applied daylight-saving
// offset is visible) and the "never construct a Date from a wall clock" rule into a
// second file where the next reader cannot see the reasoning — which is how a
// `new Date()` gets added to one of them and moves a registrar's 9:00 by however far
// the browser happens to be from Auckland. This panel stays their home: it is where a
// window is SET, and the rules that govern them are written above.

import { useState, type JSX } from 'react';
import { refusalSentence } from './copy';
import {
  api,
  DecidesApiError,
  type BallotSchedule,
  type BallotScheduleView,
  type DraftBallot,
} from '../api/client';

/** The zoneless NZ wall clock the worker's schedule route reads: exactly sixteen
 *  characters, `YYYY-MM-DDTHH:MM`. No seconds, no offset, no `Z`.
 *
 *  EXPORTED for {@link ExtendStep}, which sends the SAME shape to `POST …/extend`
 *  and needs the same shape test in front of its confirm. One pattern, so the two
 *  screens cannot start disagreeing about what a well-formed time is — a second copy
 *  is how a browser mirror drifts from the route it mirrors. */
export const NZ_WALL_CLOCK = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

/** A UTC instant as the worker states it — `2026-09-10T21:00:00.000Z`. Matched at
 *  the minute and no further: everything after it is discarded for display. */
const UTC_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/** One bound as the operator left the field. **A BLANK FIELD IS `null`, AND `null`
 *  IS AN ANSWER** — "unbounded on that side", which is a lawful window (open it by
 *  hand, or leave it open until a Scrutineer closes it) and never a validation
 *  failure. Only a NON-blank value that is not a wall clock is refused. */
type BoundRead = { ok: true; value: string | null } | { ok: false };

function readBound(raw: string): BoundRead {
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: true, value: null };
  return NZ_WALL_CLOCK.test(trimmed) ? { ok: true, value: trimmed } : { ok: false };
}

/** What this screen can tell the operator BEFORE the request, so an ordinary slip is
 *  answered in front of them rather than as a 422 — the same "legible answer in front
 *  of the trigger" the builder's client-side spec mirror keeps. */
type ScheduleProblem = 'opens_malformed' | 'closes_malformed' | 'window_inverted';

/**
 * The first problem with the two typed bounds, or `null`.
 *
 * **DELIBERATELY ONE NOTCH LOOSER THAN THE WORKER, AND THAT DIRECTION IS THE SAFE
 * ONE.** The route refuses a close that is not STRICTLY after the open
 * (`closes.epochMs <= opens.epochMs`, `src/routes/setup.ts`), so it refuses an equal
 * pair too — but it reads the two bounds under DIFFERENT ambiguity policies (the open
 * `'earlier'`, the close `'later'`), so on the morning New Zealand repeats an hour
 * the same sixteen characters name two different instants and an equal pair is
 * lawful. A `>=` test here would refuse it: over-strict in the browser, on a case
 * this file cannot evaluate without the timezone arithmetic it is forbidden. So an
 * equal pair is sent and `schedule_window_inverted` comes back if the worker
 * disagrees. Under-strict here is safe — the server re-checks every window.
 *
 * There is deliberately NO mirror of `schedule_time_does_not_exist`: knowing which
 * hour a date skips IS timezone arithmetic, and it belongs to the worker.
 */
function scheduleProblem(opensRaw: string, closesRaw: string): ScheduleProblem | null {
  const opens = readBound(opensRaw);
  if (!opens.ok) return 'opens_malformed';
  const closes = readBound(closesRaw);
  if (!closes.ok) return 'closes_malformed';
  // A string comparison, on purpose — see the header. One zone, one width, so `>` is
  // chronological and no Date is constructed.
  if (opens.value !== null && closes.value !== null && opens.value > closes.value) {
    return 'window_inverted';
  }
  return null;
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/**
 * One wall clock in words — "9:00am on 11 September 2026".
 *
 * **NOT `formatNzInstant` (ScrutineerPage.tsx:136), and it must not become it.** That
 * one takes a UTC instant and renders it in Pacific/Auckland, which is right for an
 * instant and wrong here: this value is ALREADY NZ wall clock and has no instant
 * behind it, so handing it to `new Date()` would read it in the browser's zone and
 * then shift it into NZ — moving a registrar's 9:00 by however far that machine
 * happens to be from Auckland. This is pure string work, and falls back to the raw
 * value for anything it cannot read.
 *
 * EXPORTED for {@link ExtendStep} and the setup hub's "Open now" panel, which render
 * the same wall clock in the same words. Copying eight lines of month arithmetic into
 * a second file is how one screen starts saying "19 Sep" while another says
 * "19 September" — and, worse, how one of them quietly grows a `new Date()`.
 */
export function wallClockInWords(value: string): string {
  if (!NZ_WALL_CLOCK.test(value)) return value;
  const month = MONTHS[Number(value.slice(5, 7)) - 1];
  if (month === undefined) return value;
  const day = Number(value.slice(8, 10));
  const year = value.slice(0, 4);
  const hour24 = Number(value.slice(11, 13));
  const minute = value.slice(14, 16);
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const half = hour24 < 12 ? 'am' : 'pm';
  return `${String(hour12)}:${minute}${half} on ${String(day)} ${month} ${year}`;
}

/**
 * The worker's derived instant in words — "21:00 UTC on 10 September 2026".
 *
 * **Also pure string work, and it MUST stay that way.** The seconds and milliseconds
 * are sliced off rather than parsed; nothing here constructs a Date, adds an offset
 * or asks the browser what zone it is in. It is a 24-hour clock and it is named UTC
 * out loud, so it can never be misread as the operator's own time — which is exactly
 * what a bare trailing `Z` invites.
 *
 * EXPORTED alongside {@link wallClockInWords}, and for the same reason: the pair is
 * what makes the applied daylight-saving offset visible, and a screen that carried
 * only one half would hide it.
 */
export function utcInWords(value: string): string {
  if (!UTC_INSTANT.test(value)) return value;
  const month = MONTHS[Number(value.slice(5, 7)) - 1];
  if (month === undefined) return value;
  const day = Number(value.slice(8, 10));
  const year = value.slice(0, 4);
  const hhmm = value.slice(11, 16);
  return `${hhmm} UTC on ${String(day)} ${month} ${year}`;
}

/** The window as one sentence, with each unbounded side named for what it MEANS
 *  rather than left as a gap the operator has to interpret. Exported for the setup
 *  hub's "Open now" panel, which states a running poll's window in the same words. */
export function windowInWords(opens: string | null, closes: string | null): string {
  const from =
    opens === null ? 'You open this ballot by hand' : `It opens at ${wallClockInWords(opens)}`;
  const to =
    closes === null
      ? 'it stays open until a Scrutineer closes it'
      : `it closes at ${wallClockInWords(closes)}`;
  return `${from}, and ${to}. Every time here is New Zealand time.`;
}

const PROBLEM_COPY: Record<ScheduleProblem, string> = {
  opens_malformed:
    'Give the opening time as a date and a time on the clock. Nothing has been sent.',
  closes_malformed:
    'Give the closing time as a date and a time on the clock. Nothing has been sent.',
  window_inverted:
    'The closing time is before the opening time, so there would be no time to vote. Nothing has been sent — correct one of them and save again.',
};

/**
 * The three refusal words that are THIS ROUTE'S ALONE, as sentences.
 *
 * They live HERE rather than in `copy.ts` only because the stream that wrote them had
 * not been granted it; `SETUP_REFUSAL_COPY` is their proper home, where the test that
 * pins the table against the routes' vocabulary would cover them. Everything else —
 * the gate refusals, `wrong_state`, `unreachable` — is delegated to that table
 * unchanged, so there is one sentence per wire word and no second copy of a word it
 * already carries.
 *
 * **`schedule_time_does_not_exist` HAS MOVED TO `copy.ts` AND IS DELIBERATELY NOT
 * DUPLICATED HERE.** It stopped being this route's alone: `POST …/extend` puts the
 * SAME word on the wire (`src/routes/setup.ts` — it reuses the word rather than
 * coining a second one for the same fault), so an operator can now meet it from two
 * screens and two copies of one sentence is how they start disagreeing. The
 * fall-through below reaches it unchanged, and the reasoning for why the worker spends
 * a separate word on it travels with the sentence.
 */
const SCHEDULE_REFUSAL_COPY: Record<string, string> = {
  schedule_not_editable:
    'A ballot’s voting window can only be set while it is a draft or marked ready, and this one has moved on. Nothing was changed — an open ballot is closed by a Scrutineer, not by moving its clock. Go back and open the setup again to see where it now stands.',
  schedule_invalid:
    'That voting window was not in a shape this platform can read — each time must be a real date and a real time on the clock. Nothing was changed. Correct it and try again.',
  schedule_window_inverted:
    'The closing time is not after the opening time, so there would be no time to vote. Nothing was changed — move one of them and try again.',
};

function scheduleRefusal(code: string): string {
  return SCHEDULE_REFUSAL_COPY[code] ?? refusalSentence(code);
}

/** An inline field error — a `role="alert"` span the control points at with
 *  `aria-describedby`. The WORDS carry the meaning; nothing here is a colour. The
 *  same component BallotBuilder keeps privately; written out rather than imported
 *  because that file holds it unexported and is not this stream's to change. */
function FieldError({ id, message }: { id: string; message: string }): JSX.Element {
  return (
    <span className="setup-field-error" id={id} role="alert">
      {message}
    </span>
  );
}

/**
 * ONE RECORDED BOUND — the operator's own wall clock, with the worker's derived
 * instant behind it in the muted role.
 *
 * `nz === null` is the UNBOUNDED side and is named for what it means; there is no
 * instant to show for it, because there is no time. The instant is rendered only when
 * the server sent one, so a row that somehow carries a wall clock and no instant says
 * the wall clock plainly rather than inventing the missing half.
 *
 * EXPORTED for the setup hub's "Open now" panel and {@link ExtendStep}. **The pairing
 * is the whole reason it is shared rather than re-styled per screen**: the operator's
 * wall clock in ink with the derived instant muted beneath it is the ONLY place a human
 * can see which daylight-saving offset was applied, and Synod sits eight days before
 * NZDT begins. A panel that showed a running poll's close without the instant under it
 * would be the one screen where that check went missing.
 */
export function RecordedBound({
  label,
  nz,
  utc,
  unbounded,
}: {
  label: string;
  nz: string | null;
  utc: string | null;
  unbounded: string;
}): JSX.Element {
  return (
    <p className="setup-panel-loaded">
      <strong>{label}:</strong>{' '}
      {nz === null ? (
        unbounded
      ) : (
        <>
          {wallClockInWords(nz)}, New Zealand time.
          {utc !== null ? (
            <>
              {' '}
              <span className="setup-field-hint">That is {utcInWords(utc)}.</span>
            </>
          ) : null}
        </>
      )}
    </p>
  );
}

/** What the server holds for this ballot right now, in words. Rendered above the
 *  form, so the answer to "what is this ballot set to do?" is on the screen before
 *  anything is typed. */
function RecordedWindow({ schedule }: { schedule: BallotScheduleView }): JSX.Element {
  if (schedule.mode !== 'scheduled') {
    return (
      <p className="setup-panel-loaded">
        As it stands, this ballot <strong>opens and closes by hand</strong>. You open it when Synod
        is ready, and a Scrutineer closes it.
      </p>
    );
  }
  if (schedule.opensAtNz === null && schedule.closesAtNz === null) {
    return (
      <p className="setup-panel-loaded">
        As it stands, this ballot is set to run on the clock but has{' '}
        <strong>no time fixed on either side</strong>, which comes to the same thing as opening and
        closing it by hand.
      </p>
    );
  }
  return (
    <>
      <p className="setup-panel-note">As it stands, this ballot runs on the clock.</p>
      <RecordedBound
        label="Opens"
        nz={schedule.opensAtNz}
        utc={schedule.opensAtUtc}
        unbounded="by hand, when Synod is ready."
      />
      <RecordedBound
        label="Closes"
        nz={schedule.closesAtNz}
        utc={schedule.closesAtUtc}
        unbounded="not on a clock — it stays open until a Scrutineer closes it."
      />
    </>
  );
}

interface Props {
  ballot: DraftBallot;
  /** The hub's shared lock — true while ANOTHER setup act is in flight. This
   *  control's own save is tracked separately (see `saving`). */
  busy: boolean;
}

export function SchedulePanel({ ballot, busy }: Props): JSX.Element {
  // Seeded from the list's own read, so a saved window is IN the fields on page load
  // rather than having to be retyped to be seen. These initialisers run once: after
  // that the fields are the operator's working state and nothing overwrites them
  // under a cursor. The RECORD is read separately, below.
  const [mode, setMode] = useState<BallotSchedule['mode']>(
    ballot.schedule?.mode === 'scheduled' ? 'scheduled' : 'manual',
  );
  const [opensRaw, setOpensRaw] = useState(ballot.schedule?.opensAtNz ?? '');
  const [closesRaw, setClosesRaw] = useState(ballot.schedule?.closesAtNz ?? '');
  /** THIS control's own request, deliberately not the hub's `busy`: it is what keeps
   *  the Save button mounted and focusable across its own save. */
  const [saving, setSaving] = useState(false);
  /** The window the WORKER answered on this session's save — its record of what it
   *  wrote, including the two instants it derived, never an echo of the request. */
  const [written, setWritten] = useState<BallotScheduleView | null>(null);
  /** The outcome sentence for the status region. */
  const [outcome, setOutcome] = useState<string | null>(null);
  /** The wire code of the last refusal, or `null`. */
  const [error, setError] = useState<string | null>(null);

  // `written` first: the hub does not re-read the ballot list after a save, so its
  // row is the older of the two the moment this panel writes one. `null` is the
  // deploy-skew case — a worker that predates the `schedule` object (DraftBallot).
  const recorded = written ?? ballot.schedule ?? null;

  const base = `schedule-${ballot.id}`;
  const opensId = `${base}-opens`;
  const closesId = `${base}-closes`;
  const opensHintId = `${opensId}-hint`;
  const closesHintId = `${closesId}-hint`;
  const problemId = `${base}-problem`;
  const errorId = `${base}-error`;
  // The two radios, and the two halves of each one's text. The NAME and the
  // DESCRIPTION are separate ids on purpose — see the note above the fieldset.
  const manualId = `${base}-manual`;
  const manualLabelId = `${manualId}-label`;
  const manualHelpId = `${manualId}-help`;
  const scheduledId = `${base}-scheduled`;
  const scheduledLabelId = `${scheduledId}-label`;
  const scheduledHelpId = `${scheduledId}-help`;

  const scheduled = mode === 'scheduled';
  const problem = scheduled ? scheduleProblem(opensRaw, closesRaw) : null;
  const opensBound = readBound(opensRaw);
  const closesBound = readBound(closesRaw);
  const bothBlank = opensRaw.trim() === '' && closesRaw.trim() === '';

  const summaryState =
    recorded === null
      ? 'not reported'
      : recorded.mode === 'scheduled'
        ? 'at a set time'
        : 'by hand';

  const save = async (): Promise<void> => {
    // A re-entry guard, so a double tap on hall wifi sends one request. The button is
    // not disabled by its own save — see the note on it below.
    if (saving || busy || problem !== null) return;
    let request: BallotSchedule;
    if (scheduled) {
      // `problem === null` above already means both bounds read; this is the
      // narrowing, not a second gate. A malformed value left in a field the operator
      // has since switched AWAY from must never silently block saving `manual` —
      // hence the branch rather than one check over both modes.
      if (!opensBound.ok || !closesBound.ok) return;
      request = { mode: 'scheduled', opensAtNz: opensBound.value, closesAtNz: closesBound.value };
    } else {
      request = { mode: 'manual' };
    }
    setSaving(true);
    setError(null);
    setOutcome(null);
    try {
      const answer = await api.setBallotSchedule(ballot.id, request);
      // Everything reported from here on is the SERVER's answer, not the request:
      // what was asked for and what was written are two claims, and only one of them
      // is a fact about the ballot.
      setWritten(answer.schedule);
      setOutcome(
        answer.schedule.mode === 'scheduled'
          ? `Saved. ${windowInWords(answer.schedule.opensAtNz, answer.schedule.closesAtNz)}`
          : 'Saved. You open this ballot by hand, and a Scrutineer closes it.',
      );
    } catch (err) {
      setError(err instanceof DecidesApiError ? err.code : 'unreachable');
    } finally {
      setSaving(false);
    }
  };

  /** What the Save button is described by when it has something to say: the local
   *  problem first (nothing was sent), otherwise the worker's refusal. The same
   *  idiom as the builder's Create button, which points at its blocker list. */
  const describedBy = problem !== null ? problemId : error !== null ? errorId : undefined;

  return (
    <details>
      <summary className="result-provenance-summary">
        When this ballot opens and closes — <span className="setup-field-hint">{summaryState}</span>
      </summary>

      {/* `.setup-panel` WITHOUT `.card` — the stacked-with-a-gap rule alone, so the
          disclosure's contents are spaced like the hub's panels without becoming a
          second card inside a ballot row. No new style rule is introduced. */}
      <div className="setup-panel">
        <p className="setup-panel-note">
          This ballot carries one motion or one election, so this is that item’s own voting window.
          Ordinarily you open it by hand when Synod is ready and a Scrutineer closes it. If a time
          has been fixed, set it here. Every time you type on this screen is New Zealand time — the
          clock in the hall.
        </p>

        {/* THE RECORD, BEFORE THE FORM. What the ballot is actually set to do, read
            from the server on every load — see the header. */}
        {recorded !== null ? (
          <RecordedWindow schedule={recorded} />
        ) : (
          <p className="setup-panel-note">
            This server did not report what window is recorded for this ballot. Saving below still
            sets one.
          </p>
        )}

        {/* The builder's radio shape (a <fieldset> + .setup-radio-set, each option a
            <label> WRAPPING its input so the whole row is the target). Written out
            rather than imported because BallotBuilder keeps its RadioGroup private and
            that file is not this stream's to change.

            EACH RADIO IS NAMED BY ITS OWN HEADING AND DESCRIBED BY ITS HELP, EXPLICITLY.
            The wrapping <label> alone would hand a screen reader the heading AND the
            help sentence run together as the field's NAME — "By hand — the usual way You
            open it when Synod is ready, and a Scrutineer closes it. Nothing runs on a
            clock." — which is the one thing this control must get right: a listener has
            to hear WHICH option they are on, briefly, and hear the explanation as an
            explanation. `aria-labelledby` names it from `.setup-radio-label` only and
            `aria-describedby` carries `.setup-radio-help`, the same split the
            `.setup-field` blocks below use with their own hints. The `htmlFor`/`id` pair
            states the association that was previously only implied by the nesting.
            Nothing here is visible: no copy, no element and no class changes. Do not
            "tidy" these attributes away — jsx-a11y/label-has-associated-control reads
            two levels deep, and with the text three spans down it reports "a form label
            must have accessible text" at ERROR, which is a CI failure. */}
        <fieldset className="setup-fieldset">
          <legend className="setup-legend">How does this ballot open and close?</legend>
          <div className="setup-radio-set">
            <label className="setup-radio" htmlFor={manualId}>
              <input
                id={manualId}
                className="radio"
                type="radio"
                name={`${base}-mode`}
                value="manual"
                checked={!scheduled}
                disabled={busy || saving}
                aria-labelledby={manualLabelId}
                aria-describedby={manualHelpId}
                onChange={() => setMode('manual')}
              />
              <span className="setup-radio-text">
                <span className="setup-radio-label" id={manualLabelId}>
                  By hand — the usual way
                </span>
                <span className="setup-radio-help" id={manualHelpId}>
                  You open it when Synod is ready, and a Scrutineer closes it. Nothing runs on a
                  clock.
                </span>
              </span>
            </label>
            <label className="setup-radio" htmlFor={scheduledId}>
              <input
                id={scheduledId}
                className="radio"
                type="radio"
                name={`${base}-mode`}
                value="scheduled"
                checked={scheduled}
                disabled={busy || saving}
                aria-labelledby={scheduledLabelId}
                aria-describedby={scheduledHelpId}
                onChange={() => setMode('scheduled')}
              />
              <span className="setup-radio-text">
                <span className="setup-radio-label" id={scheduledLabelId}>
                  At a set time
                </span>
                <span className="setup-radio-help" id={scheduledHelpId}>
                  Give a time to open, a time to close, or just one of the two.
                </span>
              </span>
            </label>
          </div>
        </fieldset>

        {/* `datetime-local` at step=60 holds exactly the sixteen-character wall clock
            the worker reads and applies NO zone to it, so it is sent verbatim. A BLANK
            FIELD IS NOT AN OMISSION — it is the unbounded side, and each hint says
            what that means rather than leaving it to be guessed. */}
        {scheduled ? (
          <>
            <div className="setup-field">
              <label htmlFor={opensId}>Opens (New Zealand time)</label>
              <input
                id={opensId}
                className="setup-input"
                type="datetime-local"
                step={60}
                value={opensRaw}
                disabled={busy || saving}
                aria-describedby={
                  problem === 'opens_malformed' ? `${opensHintId} ${problemId}` : opensHintId
                }
                onChange={(event) => setOpensRaw(event.target.value)}
              />
              <span className="setup-field-hint" id={opensHintId}>
                Leave this blank to open the ballot by hand when Synod is ready.
              </span>
            </div>

            <div className="setup-field">
              <label htmlFor={closesId}>Closes (New Zealand time)</label>
              <input
                id={closesId}
                className="setup-input"
                type="datetime-local"
                step={60}
                value={closesRaw}
                disabled={busy || saving}
                aria-describedby={
                  problem === 'closes_malformed' || problem === 'window_inverted'
                    ? `${closesHintId} ${problemId}`
                    : closesHintId
                }
                onChange={(event) => setClosesRaw(event.target.value)}
              />
              <span className="setup-field-hint" id={closesHintId}>
                Leave this blank and it stays open until a Scrutineer closes it.
              </span>
            </div>

            {/* What SAVING would set, in plain words, so the operator checks a
                sentence rather than a machine-shaped string. It is labelled as a
                proposal, never as the record: the record is above, and this one
                carries no instant because until the worker answers, no instant has
                been derived. */}
            {problem === null ? (
              <p className="setup-panel-note">
                {bothBlank
                  ? 'Saving this sets neither time, which is the same as opening and closing this ballot by hand.'
                  : `If you save this: ${windowInWords(
                      opensBound.ok ? opensBound.value : null,
                      closesBound.ok ? closesBound.value : null,
                    )} The exact instant each one falls on is shown above once it is saved.`}
              </p>
            ) : null}
          </>
        ) : null}

        {problem !== null ? <FieldError id={problemId} message={PROBLEM_COPY[problem]} /> : null}

        {error !== null ? (
          <p className="alert" id={errorId} role="alert">
            {scheduleRefusal(error)}
          </p>
        ) : null}

        {/* The outcome, in this control's own status channel. Present always, so a
            change is spoken without the region being freshly mounted (which several
            screen readers miss), and it carries a stage word, never a figure. */}
        <p className="setup-status" role="status">
          {outcome ?? ''}
        </p>

        <div className="setup-ballot-actions">
          {/* NOT disabled by its OWN save: an element disabled under the finger that
              pressed it drops focus to <body>, and this act is small, reversible and
              repeated. `aria-disabled` says a press will do nothing, the label says
              why, and `save`'s guard makes it true. The hub's `busy` DOES disable it —
              in that case the operator's focus is on the control they actually
              pressed, not on this one. */}
          <button
            type="button"
            className="btn btn-primary setup-primary"
            disabled={busy || problem !== null}
            aria-disabled={saving}
            aria-describedby={describedBy}
            onClick={() => void save()}
          >
            {saving ? 'Working…' : 'Save the voting window'}
          </button>
        </div>
      </div>
    </details>
  );
}
